import React, { useState, useEffect, useRef } from 'react'
import { Modal, Button, Spin } from 'antd'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import axios from 'axios'

const SameRackBoxNoSelect = ({
  id,
  rackno,
  boxno,
  isModalVisible,
  hideModal,
}) => {
  const [boxList, setBoxList] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const navigate = useNavigate()
  const requestIdRef = useRef(0)

  useEffect(() => {
    if (!isModalVisible) return

    // Only the newest open may write to state - reopening quickly used to let
    // an earlier response overwrite a later one.
    const requestId = ++requestIdRef.current

    const fetchBoxList = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await axios.get(
          `/api/v1/admin/stock-check-store/get-boxes/${rackno}/${boxno}/${id}`,
          { timeout: 30000 }
        )
        if (requestId !== requestIdRef.current) return
        const boxes = response.data?.boxes
        setBoxList(Array.isArray(boxes) ? boxes : [])
      } catch (err) {
        if (requestId !== requestIdRef.current) return
        console.error('Error fetching box list', err)
        // A failed request used to fall through to "No other boxes available",
        // which reads as a fact rather than a failure.
        setBoxList([])
        setError('Could not load the boxes for this rack.')
        toast.error(err.response?.data?.message || 'Could not load boxes.')
      } finally {
        if (requestId === requestIdRef.current) setIsLoading(false)
      }
    }

    fetchBoxList()
  }, [isModalVisible, rackno, boxno, id])

  const handleBoxClick = (boxNo) => {
    hideModal()
    navigate(
      `/admin/stock-check/store-room/${rackno}/${boxNo || 'empty'}/${id}`
    )
  }

  return (
    <Modal
      title={`Rack: ${rackno}`}
      open={isModalVisible}
      onCancel={hideModal}
      footer={
        <div className='mt-4'>
          <button
            type='button'
            className='btn btn-secondary'
            onClick={hideModal}
          >
            Close
          </button>
        </div>
      }
      // Fixed 800px overflowed the viewport on a handheld.
      width='90%'
      style={{ maxWidth: 800 }}
    >
      {isLoading ? (
        <div className='text-center py-4'>
          <Spin size='large' />
        </div>
      ) : error ? (
        <div className='text-center text-danger py-3'>{error}</div>
      ) : boxList.length > 0 ? (
        // .box-grid was never defined in any stylesheet, so the boxes just
        // flowed inline. Grid here so they wrap evenly at any width.
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.85rem',
          }}
        >
          {boxList.map((box, index) => (
            <Button
              key={box.boxNo || `empty-${index}`}
              style={{
                fontSize: '16px',
                height: '44px',
                minWidth: '90px',
                paddingLeft: '1rem',
                paddingRight: '1rem',
                backgroundColor: box.boxStatus === 1 ? '#0d6efd' : 'initial',
                color: box.boxStatus === 1 ? 'white' : 'black',
              }}
              onClick={() => handleBoxClick(box.boxNo)}
            >
              {box.boxNo || ''}
            </Button>
          ))}
        </div>
      ) : (
        <div className='text-center text-muted py-3'>
          No other boxes in this rack.
        </div>
      )}
    </Modal>
  )
}

export default SameRackBoxNoSelect
